import React, { useState } from 'react';
import { fmt, fmtChangeFixed } from '../../utils/formatters';
import {
  OrdinaryBand, PatternPoint, aboveBelow, dayLabel, dotPlotSentence, median, seriesName,
} from '../../utils/weather';
import { TD, TD_NUM, TH, TH_NUM, TIP_CLASS, TableToggle, useWidth } from './weatherUi';

// The proof chart: condition days against ordinary days, on one axis of "how
// the day did against its usual". Ordinary days are a grey band (10th to 90th
// percentile, the middle half darker) with a median tick; each clean condition
// day is a plum dot, spread in a narrow lane so none hides another.

const PLUM = '#4A2D5E';
const INK_SOFT = '#6B6670';
const HAIRLINE = '#E6E2DE';
const PAD_X = 12;
const BAND_Y = 16;       // center of the ordinary lane
const LANE_Y = 72;       // center of the condition lane
const AXIS_Y = 124;      // baseline of the axis labels
const HEIGHT = 130;
const R = 4;             // 8px dots

/** Vertical offsets that keep close dots apart: the first free slot of 0, −1, +1, −2, +2… */
export function swarm(xs: number[], gap = 9, step = 7, slots = 7): number[] {
  const order = xs.map((x, i) => [x, i]).sort((a, b) => a[0] - b[0]);
  const placed: { x: number; slot: number }[] = [];
  const out = new Array<number>(xs.length).fill(0);
  const offsets = Array.from({ length: slots }, (_, k) => (k % 2 ? -1 : 1) * Math.ceil(k / 2));
  order.forEach(([x, i]) => {
    const near = placed.filter((p) => Math.abs(p.x - x) < gap);
    const free = offsets.find((o) => !near.some((p) => p.slot === o));
    // Every slot taken: the one whose nearest neighbour is farthest away.
    const room = (o: number) => Math.min(...near.filter((p) => p.slot === o).map((p) => Math.abs(p.x - x)));
    const slot = free ?? offsets.reduce((best, o) => (room(o) > room(best) ? o : best), offsets[0]);
    placed.push({ x, slot });
    out[i] = slot * step;
  });
  return out;
}

/** Ticks every 5 or 10 points across the domain, always including zero. */
function ticks(lo: number, hi: number, width = 320): number[] {
  let step = hi - lo > 0.3 ? 0.1 : 0.05;
  // Labels need about 44px each: widen the step on a narrow chart.
  while (((hi - lo) / step) * 44 > width) step *= 2;
  const out: number[] = [];
  for (let t = Math.ceil(lo / step) * step; t <= hi + 1e-9; t += step) out.push(Math.round(t * 100) / 100);
  return out;
}

export default function DotPlot({ condition, points, ordinary, group = 'store' }: {
  condition: string; points: PatternPoint[]; ordinary: OrdinaryBand; group?: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>(320);
  const [active, setActive] = useState<number | null>(null);
  const xs = points.map((p) => p.x);
  const lo = Math.min(0, ordinary.p10, ...xs) - 0.03;
  const hi = Math.max(0, ordinary.p90, ...xs) + 0.03;
  const px = (x: number) => PAD_X + ((x - lo) / (hi - lo)) * (width - 2 * PAD_X);
  const ys = swarm(xs.map(px));
  const mid = median(xs);
  const name = seriesName(condition, group);
  const tip = active == null ? null : points[active];
  const tipLeft = tip ? Math.max(0, Math.min(width - 168, px(tip.x) - 84)) : 0;

  return (
    <div data-dotplot>
      {/* The legend: two series, named in words beside their marks. */}
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-sans text-[10px] font-medium text-[#6B6670]">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#4A2D5E]" />{name}, one dot a day, the line their median
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2 w-4 rounded-[2px] bg-[#6B6670]/25" />Ordinary days, middle 80%
        </span>
      </div>
      <div ref={ref} className="relative w-full">
        <svg
          role="img" aria-label={dotPlotSentence(condition, points, ordinary, group)}
          width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} className="block h-auto max-w-full overflow-visible font-sans"
        >
          {/* Recessive grid, and a firmer line at zero: a usual day. */}
          {ticks(lo, hi, width).map((t) => (
            <g key={t}>
              <line x1={px(t)} x2={px(t)} y1={4} y2={AXIS_Y - 12} stroke={t === 0 ? INK_SOFT : HAIRLINE} strokeWidth={1} />
              <text x={px(t)} y={AXIS_Y} textAnchor="middle" fontSize={10} fill={INK_SOFT} className="tabular-nums">
                {t === 0 ? 'usual' : fmtChangeFixed(t * 100)}
              </text>
            </g>
          ))}

          {/* Ordinary days: the band and its median. */}
          <rect data-band="p10-p90" x={px(ordinary.p10)} y={BAND_Y - 8} width={px(ordinary.p90) - px(ordinary.p10)} height={16} rx={3} fill={INK_SOFT} fillOpacity={0.14} />
          <rect data-band="q1-q3" x={px(ordinary.q1)} y={BAND_Y - 8} width={px(ordinary.q3) - px(ordinary.q1)} height={16} fill={INK_SOFT} fillOpacity={0.16} />
          <line data-median="ordinary" x1={px(ordinary.median)} x2={px(ordinary.median)} y1={BAND_Y - 10} y2={BAND_Y + 10} stroke={INK_SOFT} strokeWidth={2} strokeLinecap="round" />

          {/* Condition days: the median first, the dots over it. */}
          <line data-median="condition" x1={px(mid)} x2={px(mid)} y1={LANE_Y - 26} y2={LANE_Y + 26} stroke={PLUM} strokeWidth={2} strokeLinecap="round" />
          {points.map((p, i) => (
            <circle key={p.date} data-dot={p.date} cx={px(p.x)} cy={LANE_Y + ys[i]} r={R} fill={PLUM} stroke="#FFFFFF" strokeWidth={2} fillOpacity={active == null || active === i ? 1 : 0.55} />
          ))}
          {/* Hit targets bigger than the marks, for a finger as much as a cursor. */}
          {points.map((p, i) => (
            <circle
              key={`hit-${p.date}`} data-hit={p.date} cx={px(p.x)} cy={LANE_Y + ys[i]} r={10} fill="transparent"
              onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}
              onClick={() => setActive(active === i ? null : i)}
            />
          ))}
        </svg>
        {tip && (
          <div data-tip aria-hidden="true" className={`${TIP_CLASS} absolute w-[168px]`} style={{ left: tipLeft, top: -4 }}>
            <div className="font-medium">{dayLabel(tip.date)}</div>
            <div className="tabular-nums text-[#6B6670]">
              Sold {fmt(tip.actual)} · usual {fmt(tip.usual)}
            </div>
            <div className="tabular-nums">{fmtChangeFixed(tip.x * 100)} against its usual</div>
          </div>
        )}
      </div>
      <TableToggle className="mt-2">
        <table className="w-full font-sans text-xs">
          <caption className="sr-only">{name}, each day against its usual</caption>
          <thead>
            <tr className="border-b border-[#E6E2DE]">
              <th scope="col" className={TH}>Day</th>
              <th scope="col" className={TH_NUM}>Sold</th>
              <th scope="col" className={TH_NUM}>Usual</th>
              <th scope="col" className={TH_NUM}>Against usual</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E6E2DE]">
            {points.map((p) => (
              <tr key={p.date}>
                <td className={TD}>{dayLabel(p.date)}</td>
                <td className={TD_NUM}>{fmt(p.actual)}</td>
                <td className={TD_NUM}>{fmt(p.usual)}</td>
                <td className={TD_NUM}>{aboveBelow(p.x)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableToggle>
    </div>
  );
}
